import { cn } from "@/lib/cn";

/**
 * Smart-e brand glyph — a stylised electron-orbital "vortex" of radial
 * spokes. Reproduces the geometry of the smart-e logo in pure SVG so it
 * scales crisp, picks up `currentColor` for theming, and can be animated.
 * Tinted with `--ems-brand` so it tracks the host theme bridge.
 *
 * The wordmark "smart·e" is rendered alongside in a separate component so the
 * mark and word can be sized independently.
 */
export function SmartEMark({
  size = 28,
  spinning = false,
  className,
}: { size?: number; spinning?: boolean; className?: string }) {
  const cx = 50;
  const cy = 50;
  const lines: { x1: number; y1: number; x2: number; y2: number; w: number }[] = [];
  // 36 radial spokes of varying thickness — captures the swirl effect.
  const N = 36;
  for (let i = 0; i < N; i++) {
    const ang = (i / N) * Math.PI * 2;
    const inner = 14 + Math.sin(i * 0.7) * 2;
    const outer = 36 + (Math.cos(i * 0.55) * 4);
    const w = 3.2 + Math.sin(i * 0.9) * 1.2;
    lines.push({
      x1: cx + Math.cos(ang) * inner,
      y1: cy + Math.sin(ang) * inner,
      x2: cx + Math.cos(ang) * outer,
      y2: cy + Math.sin(ang) * outer,
      w,
    });
  }
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      className={cn("brand-mark", spinning && "animate-spin-slow", className)}
      style={{ color: "var(--ems-brand)" }}
      aria-hidden
    >
      {/* Outer faint orbit ring */}
      <circle cx={cx} cy={cy} r={42} stroke="currentColor" strokeOpacity={0.25} strokeWidth={1.2} fill="none" />
      {/* Radial spokes */}
      <g>
        {lines.map((l, i) => (
          <line
            key={i}
            x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2}
            stroke="currentColor"
            strokeWidth={l.w}
            strokeLinecap="round"
            opacity={0.55 + (i % 3) * 0.15}
          />
        ))}
      </g>
      {/* Inner core dot */}
      <circle cx={cx} cy={cy} r={8} fill="currentColor" opacity={0.92} />
      <circle cx={cx} cy={cy} r={5} fill="#0A1A2E" opacity={0.55} />
    </svg>
  );
}

/**
 * The full smart-e wordmark — "smart" in current text colour and the trailing
 * "·e" in the brand accent. Designed to sit beside the SmartEMark glyph.
 */
export function SmartEWordmark({
  className,
}: { className?: string }) {
  return (
    <span
      className={cn(
        "select-none font-semibold tracking-tight",
        className,
      )}
    >
      <span className="text-text">smart</span>
      <span className="px-[0.18em] text-text-faint">·</span>
      <span
        style={{
          color: "var(--ems-brand)",
          textShadow: "0 0 6px color-mix(in srgb, var(--ems-brand) 35%, transparent)",
        }}
      >
        e
      </span>
    </span>
  );
}
